import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import * as moment from 'moment';
import {Moment} from 'moment';
import * as Showdown from 'showdown';

import {Company} from 'classes/company';
import {Construct} from 'classes/construct';
import {ContentUsage, Dialog} from 'classes/dialog';
import {Language} from 'classes/language';
import {Question} from 'classes/question';
import {COURSE_DUMMY, QUESTIONNAIRE_DUMMY, TargetGroup, TARGETGROUP_DUMMIES} from 'shared/models/target-groups/target-group';
import {ConstructListComponent} from './construct-list/construct-list.component';
import {DialogDatepickerComponent} from './dialog-datepicker/dialog-datepicker.component';
import {DialogsDetailsService} from './services/dialogs-details.service';

@Component({
  selector: 'dialogs-details',
  templateUrl: './dialogs-details.component.html',
  styleUrls: ['./dialogs-details.component.css'],
})
export class DialogsDetailsComponent implements OnInit {

  @ViewChild('constructList') public constructList: ConstructListComponent;
  @ViewChild('startDatepicker') public startDatepicker: DialogDatepickerComponent;
  @ViewChild('endDatepicker') public endDatepicker: DialogDatepickerComponent;
  public languages: Array<Language> = Language.defaultLanguages;
  public language: string = this.languages[0].code;
  public allTargetGroups: Array<TargetGroup> = [];
  public showMarkdownOutput: boolean = false;
  private markdownOutput: {
    de: string,
    en: string,
  };
  private converter: Showdown.Converter = new Showdown.Converter();
  public caption: string = 'Dialog erstellen';
  public dialog: Dialog = new Dialog();
  private companies: Array<Company>;
  private intervalUnits: {
    DAY: moment.unitOfTime.Diff;
    WEEK: moment.unitOfTime.Diff;
    MONTH: moment.unitOfTime.Diff;
    YEAR: moment.unitOfTime.Diff
  } = {
    DAY: 'days',
    WEEK: 'weeks',
    MONTH: 'months',
    YEAR: 'years',
  };

  private contentUsages: Array<ContentUsage> = [];
  public contentOnly: boolean = false;
  public questionnaireOnly: boolean = false;
  private contentDummyTargetGroup = null;
  private questionnaireDummyTargetGroup = null;

  constructor(private dialogsDetailsService: DialogsDetailsService,
              private router: Router,
              private activatedRoute: ActivatedRoute) {
    this.converter.setFlavor('github')
  }

  public ngOnInit(): void {
    this.markdownOutput = {de: '', en: ''};

    this.activatedRoute.params.map((params) => params['dialogId'])
      .subscribe((dialogId: number) => {
        if (dialogId) {
          this.caption = 'Dialog bearbeiten';
          this.showMarkdownOutput = true;
          this.dialogsDetailsService.getDialogById(dialogId)
            .subscribe((dialog) => {
              if (!dialog.targetGroup) {
                dialog.targetGroup = new TargetGroup();
              }
              this.dialog = dialog;
              this.updateMarkdown();
              this.startDatepicker.updateUtcDate(this.dialog.startDate);
              this.endDatepicker.updateUtcDate(this.dialog.endDate);
              this.contentOnly = dialog.targetGroup.title === COURSE_DUMMY;
              this.questionnaireOnly = dialog.targetGroup.title === QUESTIONNAIRE_DUMMY;
            });
            this.dialogsDetailsService.getContentUsages(dialogId)
              .subscribe((contentUsages: Array<ContentUsage>) => {
                this.contentUsages = contentUsages;
                // console.log("co %o", this.contentUsages);
              });
          }
      });
    this.dialogsDetailsService.getAllTargetGroups()
      .subscribe((allTargetGroups) => {
        const contentTargetGroup = allTargetGroups.find((group) => { return group.title === COURSE_DUMMY});
        if (contentTargetGroup) {
          this.contentDummyTargetGroup = contentTargetGroup;
        }
        const questionnaireTargetGroup = allTargetGroups.find((group) => { return group.title === QUESTIONNAIRE_DUMMY});
        if (questionnaireTargetGroup) {
          this.questionnaireDummyTargetGroup = questionnaireTargetGroup;
        }
        this.allTargetGroups = allTargetGroups
          .filter((group) => { return !TARGETGROUP_DUMMIES.includes(group.title)});

      });
  }

  public createConstruct(): void {
    this.router.navigateByUrl('/constructs/details');
  }

  private saveDialog(): void { // tslint:disable-line
    if (this.contentOnly) {
      this.dialog.targetGroup = this.contentDummyTargetGroup;
    } else if (this.questionnaireOnly) {
      this.dialog.targetGroup = this.questionnaireDummyTargetGroup;
    } else {
      if ( this.dialog.targetGroup._id == this.contentDummyTargetGroup._id ) {
        this.dialog.targetGroup = new TargetGroup();
      }
    }
    this.dialogsDetailsService.saveDialog(this.dialog)
      .subscribe((response) => {
        this.router.navigateByUrl('/dialogs');
      });
  }

  private updateDialog(): void { // tslint:disable-line
    if (this.contentOnly) {
      this.dialog.targetGroup = this.contentDummyTargetGroup;
    } else if (this.questionnaireOnly) {
      this.dialog.targetGroup = this.questionnaireDummyTargetGroup;
    } else{
      if ( this.dialog.targetGroup._id == this.contentDummyTargetGroup._id ) {
        this.dialog.targetGroup = new TargetGroup();
      }
    }

    this.dialogsDetailsService.updateDialog(this.dialog)
      .subscribe((response) => {
        this.router.navigateByUrl('/dialogs');
      });
  }

  public setLanguage(language: Language): void { // tslint:disable-line
    this.language = language.code;
  }

  private removeQuestionFromDialog(question: Question): void { // tslint:disable-line
    const questionIndex: number = this.dialog.questions.indexOf(question);

    if (questionIndex > -1) {
      this.dialog.questions.splice(questionIndex, 1);
      this.constructList.addQuestionToConstruct(question);
    }
  }

  public addQuestionToDialog(question: Question): void {  // tslint:disable-line
    this.dialog.questions.push(question);
  }

  public isQuestionsEmpty(): boolean { // tslint:disable-line
    return this.dialog.questions.length === 0;
  }

  public get selectedConstructs(): Array<Construct> {
    const constructs: Array<Construct> = [];
    const constructsAdded: any = {}; // tslint:disable-line:no-any

    for (const question of this.dialog.questions) {
      const construct: Construct = question.construct;

      if (!constructsAdded[construct._id]) {
        constructs.push(construct);
        constructsAdded[construct._id] = true;
      }
    }

    return constructs;
  }

  private getQuestionsByConstructId(constructId: number): Array<Question> {
    const questions: Array<Question> = [];

    for (const question of this.dialog.questions) {
      if (question.construct._id === constructId) {
        questions.push(question);
      }
    }

    return questions;
  }

  public handleStartDateChange(date: Date): void {
    this.dialog.startDate = date;

    // Don't allow start date to be after the end date
    if (this.endDatepicker.utcDate < this.startDatepicker.utcDate) {
      this.endDatepicker.utcDate = new Date(this.startDatepicker.utcDate.getTime());
      this.dialog.endDate = new Date(this.dialog.startDate.getTime());
    }
  }

  public handleEndDateChange(date: Date): void { // tslint:disable-line
    this.dialog.endDate = date;
  }

  private setMarkdownOutput(value: boolean): void {
    this.showMarkdownOutput = value;
  }

  private updateMarkdown(): void {
    this.markdownOutput[this.language] = this.converter.makeHtml(this.dialog.description[this.language]);
  }

  private get repetitionCount(): number {
    const startDate: Moment = moment(this.dialog.startDate);
    const endDate: Moment = moment(this.dialog.endDate);
    const intervalUnit: moment.unitOfTime.Diff = this.intervalUnits[this.dialog.intervalUnit];
    const intervalValue: number = this.dialog.intervalValue;

    return 1 + Math.floor(endDate.diff(startDate, intervalUnit) / intervalValue);
  }

  public get prettyRepetitionCount(): string {
    if (this.dialog.intervalValue === null || !this.dialog.intervalUnit === null) {
      return '';
    }

    if (!this.dialog.endDate) {
      return '∞';
    }

    return this.repetitionCount.toString();
  }

  // usages by contents

  public get isUsedByActiveContent(): boolean {
    return this.contentUsages.some((contentUsage) => {
      return contentUsage.active;
    });
  }

  public get activeContents(): Array<ContentUsage> {
    return this.contentUsages.filter((contentUsage) => {
      return contentUsage.active;
    });
  }

  public openContent(contentId: number): void {
    this.router.navigate(['contents', 'details', {contentId: contentId}]);
  }

  public targetGroupChanged(ev): void {
    if (ev == this.contentDummyTargetGroup._id) {
      this.contentOnly = true;
    }
    if (ev == this.questionnaireDummyTargetGroup._id) {
      this.questionnaireOnly = true;
    }
  }
}



// WEBPACK FOOTER //
// /usr/src/app/src/app/modules/dialogs/dialogs-details/dialogs-details.component.ts